// src/features/database-objects/services/pinnedObjects/groupPinnedChildrenByDatabase.ts
/**
 * Pure helper that groups a flat ordered `PinnedObjectRef[]` into per-database
 * "areas" (runs of consecutive pins from the same database) for render-time
 * visual grouping. See the design spec:
 *   Docs/superpowers/specs/2026-05-26-object-explorer-pinning-design.md
 *   §"Visual grouping — database area (render-time wrapper)"
 *
 * Side-effect-free; safe to call from anywhere in the renderer.
 */

import type { PinnedObjectRef } from '../../../../types/electron';

export type PinnedAreaRun = {
  database: string;
  refs: PinnedObjectRef[];
  /**
   * false when the connection has only one accessible DB (header is redundant),
   * or when this run is a single pinned-DB node with no same-DB siblings
   * (i.e. `isPinnedDbAlone === true` — the area wrapper itself is also omitted).
   */
  showHeader: boolean;
  /** true when the run is exactly `[{ kind: 'database' }]` AND no other run shares its database. Renderer omits the area wrapper. */
  isPinnedDbAlone: boolean;
};

export type GroupOptions = {
  /**
   * Number of databases the user has access to on this connection (typically
   * the length of the cached Databases-folder listing). When `=== 1`, every
   * run is emitted with `showHeader: false` because there's nothing to
   * disambiguate against. When the count is not yet known (e.g. the Databases
   * folder hasn't loaded yet) callers should pass `0` or any value other than
   * `1` — headers will show by default since the strict `=== 1` check is the
   * only condition that suppresses them.
   */
  accessibleDatabaseCount: number;
};

export function groupPinnedChildrenByDatabase(
  refs: PinnedObjectRef[],
  opts: GroupOptions,
): PinnedAreaRun[] {
  if (refs.length === 0) return [];

  // First pass: build consecutive-same-DB runs.
  const runs: { database: string; refs: PinnedObjectRef[] }[] = [];
  for (const ref of refs) {
    const last = runs[runs.length - 1];
    if (last && last.database === ref.database) {
      last.refs.push(ref);
    } else {
      runs.push({ database: ref.database, refs: [ref] });
    }
  }

  // Second pass: count occurrences of each DB across all runs to detect
  // "alone" status (a singleton pinned-DB whose DB appears in no other run).
  const dbRunCount = new Map<string, number>();
  for (const r of runs) {
    dbRunCount.set(r.database, (dbRunCount.get(r.database) ?? 0) + 1);
  }

  const hideHeaders = opts.accessibleDatabaseCount === 1;

  return runs.map((r): PinnedAreaRun => {
    const isPinnedDbAlone =
      r.refs.length === 1 &&
      r.refs[0].kind === 'database' &&
      (dbRunCount.get(r.database) ?? 0) === 1;
    return {
      database: r.database,
      refs: r.refs,
      showHeader: !hideHeaders && !isPinnedDbAlone,
      isPinnedDbAlone,
    };
  });
}
